// src/screens/statistics/OrderStatistics.tsx
import { useState, useMemo } from "react";
import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachMonthOfInterval,
  subMonths,
} from "date-fns";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { OrderState } from "../../../entity/Order";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Box,
  Text,
  Heading,
  Select,
  HStack,
  VStack,
  useColorModeValue,
  Card,
  CardHeader,
  CardBody,
  Stack,
} from "@chakra-ui/react";
import { COLORS } from "../../../utils/Colors";
import { selectOperations } from "../../../store/selectors/OperationsSelector";
import { OperationType } from "../../../entity/Operation";

interface MonthlyStats {
  month: string;
  revenue: number;
  expense: number;
  profit: number;
}

export const OrderStatistics = () => {
  const { t } = useTranslation();
  const operations = useSelector(selectOperations);
  const [monthsToShow, setMonthsToShow] = useState<number>(12);

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const theadBg = useColorModeValue("gray.50", "gray.700");

  // Calculate monthly statistics
  const monthlyStats = useMemo(() => {
    const stats: MonthlyStats[] = [];
    const now = new Date();
    const startDate = subMonths(now, monthsToShow - 1);
    const months = eachMonthOfInterval({
      start: startOfMonth(startDate),
      end: endOfMonth(now),
    }).reverse();

    months.forEach((month) => {
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);
      const monthKey = format(month, "yyyy-MM");

      const monthOperations = operations.filter((operations) => {
        if (!operations.date) return false;
        const operationDate =
          typeof operations.date === "string"
            ? parseISO(operations.date)
            : new Date(operations.date);
        return operationDate >= monthStart && operationDate <= monthEnd;
      });

      const revenue = monthOperations
        .filter(
          (operation) =>
            operation.type === OperationType.SELL ||
            operation.type === OperationType.TRANSFER_IN,
        )
        .reduce((sum, operation) => sum + (operation.amount || 0), 0);

      const expense = monthOperations
        .filter(
          (operation) =>
            operation.type !== OperationType.SELL &&
            operation.type !== OperationType.TRANSFER_IN,
        )
        .reduce((sum, operation) => sum + (operation.amount || 0), 0);

      stats.push({
        month: format(month, "MMMM yyyy"),
        revenue,
        expense,
        profit: revenue - expense,
      });
    });

    return stats;
  }, [operations, monthsToShow]);

  const totalRevenue = monthlyStats.reduce(
    (sum, month) => sum + month.revenue,
    0,
  );
  const totalExpense = monthlyStats.reduce(
    (sum, month) => sum + month.expense,
    0,
  );
  const totalProfit = totalRevenue - totalExpense;

  return (
    <Box p={4}>
      <Card variant="outline" mb={6}>
        <CardHeader>
          <Stack
            direction={{ base: "column", md: "row" }}
            spacing={4}
            justify="space-between"
            align={{ base: "stretch", md: "center" }}
          >
            <Heading size="lg">{t("sidebar.ordersStats")}</Heading>
            <Select
              value={monthsToShow}
              onChange={(e) => setMonthsToShow(Number(e.target.value))}
              maxW={{ base: "100%", md: "200px" }}
            >
              <option value={6}>Last 6 months</option>
              <option value={12}>Last 12 months</option>
              <option value={24}>Last 2 years</option>
            </Select>
          </Stack>
        </CardHeader>
        <CardBody>
          <HStack
            spacing={{ base: 4, md: 6 }}
            mb={6}
            flexWrap="wrap"
            gap={4}
            justifyContent={{ base: "center", md: "flex-start" }}
          >
            <StatCard
              label="Total Revenue"
              value={totalRevenue.toFixed(2)}
              color="green.500"
            />
            <StatCard
              label="Total Expense"
              value={totalExpense.toFixed(2)}
              color="red.500"
            />
            <StatCard
              label="Net Profit"
              value={totalProfit.toFixed(2)}
              color={totalProfit >= 0 ? "green.500" : "red.500"}
            />
          </HStack>
        </CardBody>
      </Card>

      <TableContainer
        borderWidth="1px"
        borderRadius="lg"
        overflowX="auto"
        whiteSpace="nowrap"
        maxW="100%"
        sx={{
          "&::-webkit-scrollbar": {
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
        }}
      >
        <Table variant="simple" minW="600px">
          <Thead bg={theadBg}>
            <Tr>
              <Th>Month</Th>
              <Th isNumeric>Revenue</Th>
              <Th isNumeric>Expense</Th>
              <Th isNumeric>Profit/Loss</Th>
            </Tr>
          </Thead>
          <Tbody>
            {monthlyStats.map((stat) => (
              <Tr key={stat.month} _hover={{ bg: hoverBg }}>
                <Td fontWeight="medium" color={COLORS.Text.Primary}>
                  {stat.month}
                </Td>
                <Td isNumeric color="green.500">
                  {stat.revenue.toFixed(2)}
                </Td>
                <Td isNumeric color="red.500">
                  {stat.expense.toFixed(2)}
                </Td>
                <Td
                  isNumeric
                  color={stat.profit >= 0 ? "green.500" : "red.500"}
                >
                  {stat.profit.toFixed(2)}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const StatCard = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) => (
  <Card flex={1} variant="outline">
    <CardBody>
      <VStack spacing={1} align="stretch">
        <Text fontSize="sm" color="gray.500">
          {label}
        </Text>
        <Text fontSize="xl" fontWeight="bold" color={color}>
          {value}
        </Text>
      </VStack>
    </CardBody>
  </Card>
);

export default OrderStatistics;
